
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { NewsArticle } from "@/pages/admin/news/types";
import { ArticleDialog } from "./ArticleDialog";

export function NewsSection() {
  const [newsArticles, setNewsArticles] = useState<NewsArticle[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<NewsArticle | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});

  useEffect(() => {
    // Φορτώνουμε τα άρθρα από το localStorage κατά την αρχικοποίηση
    const storedArticles = localStorage.getItem('newsArticles');
    if (storedArticles) {
      try {
        const parsedArticles: NewsArticle[] = JSON.parse(storedArticles);
        // Φιλτράρισμα δημόσιων άρθρων, παίρνουμε τα πιο πρόσφατα
        const publicNewsArticles = parsedArticles
          .filter(article => article.isPublic && article.category === "news")
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
          .slice(0, 3); // Παίρνουμε μόνο τα 3 πιο πρόσφατα
        
        setNewsArticles(publicNewsArticles);
      } catch (error) {
        console.error("Error parsing stored articles:", error);
        // Αν υπάρχει σφάλμα, φορτώνουμε τα demo δεδομένα
        loadDemoData();
      }
    } else {
      // Αν δεν υπάρχουν αποθηκευμένα άρθρα, φορτώνουμε τα demo δεδομένα
      loadDemoData();
    }
  }, []);

  const loadDemoData = () => {
    // Demo δεδομένα για την παρουσίαση
    const demoNews: NewsArticle[] = [
      {
        id: 1,
        title: "Νέα εργαλεία λογιστικής υποστήριξης",
        description: "Ανακαλύψτε τα νέα εργαλεία που θα διευκολύνουν τη λογιστική σας διαχείριση.",
        content: "Η εξέλιξη της τεχνολογίας έχει φέρει επανάσταση στον τρόπο με τον οποίο οι επιχειρήσεις διαχειρίζονται τη λογιστική τους. Τα νέα εργαλεία λογιστικής που κυκλοφόρησαν πρόσφατα προσφέρουν αυτοματοποίηση, μεγαλύτερη ακρίβεια και εξοικονόμηση χρόνου.\n\nΜε τη χρήση τεχνητής νοημοσύνης, τα νέα συστήματα μπορούν να αναγνωρίζουν αυτόματα δεδομένα από τιμολόγια, να κατηγοριοποιούν συναλλαγές και να προτείνουν βελτιστοποιήσεις για τη φορολογική στρατηγική.\n\nΕπιπλέον, η ενσωμάτωση cloud τεχνολογίας επιτρέπει την πρόσβαση στα λογιστικά δεδομένα από οπουδήποτε, διευκολύνοντας την απομακρυσμένη εργασία και τη συνεργασία μεταξύ των ομάδων.",
        date: "10/07/2023",
        category: "news" as "news",
        image: "https://images.unsplash.com/photo-1581091226033-d5c48150dbaa?q=80&w=300&auto=format&fit=crop",
        isPublic: true
      },
      {
        id: 2,
        title: "Επιδοτήσεις ΕΣΠΑ για επιχειρήσεις",
        description: "Νέο πρόγραμμα επιδοτήσεων για μικρομεσαίες επιχειρήσεις ανακοινώθηκε σήμερα.",
        content: "Το Υπουργείο Ανάπτυξης ανακοίνωσε ένα νέο πρόγραμμα επιδοτήσεων ΕΣΠΑ που απευθύνεται ειδικά σε μικρομεσαίες επιχειρήσεις. Το πρόγραμμα, συνολικού προϋπολογισμού 500 εκατομμυρίων ευρώ, στοχεύει στην ενίσχυση της ανταγωνιστικότητας και της καινοτομίας.\n\nΟι επιχειρήσεις μπορούν να λάβουν επιδοτήσεις έως και 60% για επενδύσεις σε νέες τεχνολογίες, ψηφιακό μετασχηματισμό και πράσινη μετάβαση. Ιδιαίτερη έμφαση δίνεται σε επιχειρήσεις που δραστηριοποιούνται στους τομείς της πληροφορικής, της ενέργειας και της αγροτικής παραγωγής.\n\nΟι αιτήσεις θα ξεκινήσουν τον επόμενο μήνα και θα παραμείνουν ανοιχτές για 60 ημέρες. Οι ενδιαφερόμενοι μπορούν να βρουν περισσότερες πληροφορίες στην επίσημη ιστοσελίδα του προγράμματος.",
        date: "05/07/2023",
        category: "news" as "news",
        image: "https://images.unsplash.com/photo-1643057752896-b6fb4ee01bd9?q=80&w=300&auto=format&fit=crop",
        isPublic: true
      },
      {
        id: 3,
        title: "Αλλαγές στη φορολογική νομοθεσία",
        description: "Ενημερωθείτε για τις πρόσφατες αλλαγές στη φορολογική νομοθεσία που επηρεάζουν τις επιχειρήσεις.",
        content: "Σημαντικές αλλαγές στη φορολογική νομοθεσία τέθηκαν πρόσφατα σε ισχύ, επηρεάζοντας τόσο τις επιχειρήσεις όσο και τους ιδιώτες. Οι αλλαγές περιλαμβάνουν μειώσεις στους συντελεστές φορολογίας εισοδήματος για τις μικρομεσαίες επιχειρήσεις, καθώς και νέα κίνητρα για επενδύσεις σε καινοτόμες τεχνολογίες.\n\nΕπιπλέον, εισάγονται νέες ρυθμίσεις για την ηλεκτρονική τιμολόγηση και τα ηλεκτρονικά βιβλία, με στόχο την καταπολέμηση της φοροδιαφυγής και τη διευκόλυνση των φορολογικών ελέγχων.\n\nΟι επιχειρήσεις θα πρέπει να προσαρμοστούν στις νέες απαιτήσεις εντός των επόμενων έξι μηνών. Συνιστάται η συνεργασία με εξειδικευμένους φοροτεχνικούς για την ομαλή μετάβαση στο νέο φορολογικό καθεστώς.",
        date: "01/07/2023",
        category: "news" as "news",
        image: "https://images.unsplash.com/photo-1586094332066-fc72f8427986?q=80&w=300&auto=format&fit=crop",
        isPublic: true
      }
    ];
    setNewsArticles(demoNews);
  };

  const handleReadMore = (article: NewsArticle) => {
    setSelectedArticle(article);
    setIsDialogOpen(true);
  };

  const handleImageError = (articleId: number) => {
    setImageErrors(prev => ({
      ...prev,
      [articleId]: true
    }));
  };

  return (
    <section id="news" className="py-16">
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="text-3xl font-bold mb-8">Τελευταία Νέα</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {newsArticles.map(news => (
            <Card key={news.id} className="overflow-hidden">
              <div className="w-full h-48 overflow-hidden">
                {news.image && !imageErrors[news.id] ? (
                  <img 
                    src={news.image} 
                    alt={news.title} 
                    className="w-full h-full object-cover transition-transform hover:scale-105"
                    onError={() => handleImageError(news.id)}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                    <p className="text-gray-500">Δεν υπάρχει εικόνα</p>
                  </div>
                )}
              </div>
              <CardHeader className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <Badge variant="outline">Νέα</Badge>
                  <span className="text-xs text-muted-foreground">{news.date}</span>
                </div>
                <CardTitle className="text-xl">{news.title}</CardTitle>
                <CardDescription>{news.description}</CardDescription>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <Button variant="link" className="p-0" onClick={() => handleReadMore(news)}>
                  Διαβάστε περισσότερα
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Button variant="outline">Περισσότερα νέα</Button>
        </div>
      </div>

      <ArticleDialog 
        article={selectedArticle}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </section>
  );
}
